/**
 * The screen an account lands on when it is still using a password somebody
 * else chose for it.
 *
 * It replaces the application rather than appearing inside it. That is a
 * presentation decision matching a server-side one: while
 * `mustChangePassword` is set, `authed` refuses every route except this form,
 * `/auth/me` and sign-out. Rendering a dismissible banner over a working
 * application would have described a restriction that the API was already
 * enforcing, so every click behind the banner would have failed with a
 * permissions error about the user's own password.
 *
 * The policy is stated up front rather than discovered by rejection. The
 * server is still the authority — it re-checks all of it, and a problem it
 * reports is shown verbatim — but a rule the user can read before typing is
 * worth more than the same rule delivered as an error afterwards.
 */

import { useId, useState, type FormEvent } from 'react'
import { api, ApiError, unreachableMessage } from '@/lib/api'
import { useSession } from '@/lib/session'
import { SchoolMark } from '@/components/SchoolMark'
import './login.css'

const MIN_LENGTH = 12

export function ChangePasswordPage() {
  const { session, refresh, signOut } = useSession()
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')
  const [reveal, setReveal] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)

  const currentId = useId()
  const nextId = useId()
  const confirmId = useId()
  const errorId = useId()

  // Checked here only to spare a round trip and to keep the message next to
  // the field. `checkPasswordPolicy` on the server is what decides.
  const tooShort = next.length > 0 && next.length < MIN_LENGTH
  const mismatch = confirm.length > 0 && next !== confirm
  const ready = current.length > 0 && next.length >= MIN_LENGTH && next === confirm

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    if (!ready) return

    setSubmitting(true)
    setProblem(null)
    try {
      await api.changePassword({ currentPassword: current, newPassword: next })
      // Changing the password revokes every other session, including any the
      // bootstrap password was used to open elsewhere. This one survives, so
      // refreshing is enough to clear the flag and land in the application.
      await refresh()
    } catch (caught) {
      // The server returns field-level detail for a weak password, which is
      // more specific than anything worth writing here.
      setProblem(
        caught instanceof ApiError
          ? caught.fieldErrors.newPassword ?? caught.fieldErrors.currentPassword ?? caught.message
          : unreachableMessage(),
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="login login--single" data-portal="staff">
      <main className="login__panel" aria-labelledby="change-password-heading">
        <header className="login__brand">
          <SchoolMark className="login__mark" />
          <div className="login__wordmark">
            <span className="login__wordmark-name">
              Cornerstone <strong>Education</strong>
            </span>
            <span className="login__wordmark-tagline">People · Progress · Brighter Futures</span>
          </div>
        </header>

        <div className="login__body">
          <h1 className="login__heading" id="change-password-heading">
            Choose your password
          </h1>
          <p className="login__subtitle">
            {session?.user.displayName
              ? `Welcome, ${session.user.firstName}. `
              : ''}
            Your account was set up with a temporary password. Pick your own before continuing.
          </p>

          <form className="login__form" onSubmit={onSubmit} noValidate>
            {problem && (
              <p className="login__message login__message--error" id={errorId} role="alert">
                {problem}
              </p>
            )}

            <div className="field">
              <label className="field__label" htmlFor={currentId}>
                Temporary password
              </label>
              <div className="field__control">
                <input
                  id={currentId}
                  type={reveal ? 'text' : 'password'}
                  autoComplete="current-password"
                  value={current}
                  onChange={(event) => setCurrent(event.target.value)}
                  aria-describedby={problem ? errorId : undefined}
                  required
                />
              </div>
            </div>

            <div className="field">
              <label className="field__label" htmlFor={nextId}>
                New password
              </label>
              <div className="field__control">
                <input
                  id={nextId}
                  type={reveal ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={next}
                  onChange={(event) => setNext(event.target.value)}
                  aria-invalid={tooShort || undefined}
                  required
                />
                <button
                  className="field__reveal"
                  type="button"
                  onClick={() => setReveal((shown) => !shown)}
                  aria-pressed={reveal}
                >
                  {reveal ? 'Hide' : 'Show'}
                </button>
              </div>
              <p className="field__hint">
                {tooShort
                  ? `${MIN_LENGTH - next.length} more character${MIN_LENGTH - next.length === 1 ? '' : 's'} needed.`
                  : `At least ${MIN_LENGTH} characters. A phrase you can remember beats a short password with symbols in it. Do not include your name or email address.`}
              </p>
            </div>

            <div className="field">
              <label className="field__label" htmlFor={confirmId}>
                Confirm new password
              </label>
              <div className="field__control">
                <input
                  id={confirmId}
                  type={reveal ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(event) => setConfirm(event.target.value)}
                  aria-invalid={mismatch || undefined}
                  required
                />
              </div>
              {mismatch && <p className="field__hint field__hint--error">These do not match.</p>}
            </div>

            <button className="button button--primary button--block" type="submit" disabled={!ready || submitting}>
              {submitting ? 'Saving…' : 'Set password and continue'}
            </button>

            <p className="login__note">
              Signing in anywhere else will end when you save this. That is deliberate: if the temporary
              password reached anyone it should not have, their session goes with it.
            </p>
          </form>
        </div>

        <footer className="login__footer">
          <button className="login__portal-toggle" type="button" onClick={() => void signOut()}>
            Sign out instead
          </button>
        </footer>
      </main>
    </div>
  )
}
